import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatDateLong, formatTimeTo12Hour } from '../lib/utils';
import { createDateInTimezone } from '../utils/timezone';
import { CartItemInput } from '../services/cart/cartTypes';

interface TimeSlot {
  windowId: string;
  windowName?: string;
  windowType: 'daily' | 'weekly' | 'monthly';
  startDateTime: string;
  endDateTime: string;
  price: number;
  availableQuantity: number;
  totalQuantity: number;
}

interface RentalWindow {
  _id: string;
  name?: string;
  windowType: 'monthly' | 'weekly' | 'daily';
  startTime?: string;
  endTime?: string;
  intervalMinutes?: number;
}

interface GridSlot {
  time: string; // "08:00 AM"
  startDateTime: Date;
  endDateTime: Date;
  windowId: string;
  price: number;
  availableQuantity: number;
  totalQuantity: number;
  status: 'available' | 'in-cart' | 'reserved' | 'unavailable';
  renterName?: string;
}

interface TimeSlotGridProps {
  listingName: string;
  selectedDate: Date;
  windows: RentalWindow[];
  slots: TimeSlot[];
  cartItems: CartItemInput[];
  timezone: string;
  onPrevDay: () => void;
  onNextDay: () => void;
}

const TimeSlotGrid: React.FC<TimeSlotGridProps> = ({
  listingName,
  selectedDate,
  windows,
  slots,
  cartItems,
  timezone,
  onPrevDay,
  onNextDay,
}) => {
  const navigate = useNavigate();

  // Get the daily window to determine time range
  const dailyWindow = useMemo(() => {
    return windows.find(w => w.windowType === 'daily');
  }, [windows]);

  // Generate grid slots
  const gridSlots = useMemo<GridSlot[]>(() => {
    if (!dailyWindow || !dailyWindow.startTime || !dailyWindow.endTime) {
      return [];
    }

    const [startHour, startMin] = dailyWindow.startTime.split(':').map(Number);
    const [endHour, endMin] = dailyWindow.endTime.split(':').map(Number);
    const intervalMinutes = dailyWindow.intervalMinutes || 60;

    const gridSlots: GridSlot[] = [];
    let currentHour = startHour;
    let currentMin = startMin;

    // The window's startTime/endTime are wall-clock times in the listing's
    // timezone, and the selected day is whichever calendar day the buyer is
    // browsing. year/month/day come off the browser's own clock (the day the
    // buyer picked), but hour/minute have to be resolved through the
    // listing's timezone, not the browser's, to land on the same UTC instant
    // the backend generated for this slot.
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const day = selectedDate.getDate();

    while (true) {
      // Capture current values for use in closures (fixes eslint no-loop-func warning)
      const slotHour = currentHour;
      const slotMin = currentMin;

      // Calculate end of this interval
      let intervalEndMin = currentMin + intervalMinutes;
      let intervalEndHour = currentHour;

      while (intervalEndMin >= 60) {
        intervalEndMin -= 60;
        intervalEndHour += 1;
      }

      // Stop if we've passed the end time
      if (intervalEndHour > endHour || (intervalEndHour === endHour && intervalEndMin > endMin)) {
        break;
      }

      // Build the real UTC instant for this wall-clock time in the listing's
      // timezone — the same instant the backend would generate for this slot.
      const startDate = createDateInTimezone(year, month, day, slotHour, slotMin, timezone);
      const endDate = createDateInTimezone(year, month, day, intervalEndHour, intervalEndMin, timezone);

      // The label shows the intended wall-clock time directly, rather than
      // reading it back off a Date in the browser's timezone.
      const timeLabel = formatTimeTo12Hour(
        `${String(slotHour).padStart(2, '0')}:${String(slotMin).padStart(2, '0')}`
      );

      // Find matching slot from API by the actual instant, not by comparing
      // browser-local hour/minute against it.
      const matchingSlot = slots.find(slot => {
        return new Date(slot.startDateTime).getTime() === startDate.getTime();
      });

      // Check if in cart - check if this grid slot falls within any cart item's time range.
      // The range comparison alone is the authoritative check; no separate
      // same-day pre-filter is needed (and a UTC-calendar-day one would be
      // wrong here anyway, for the same reason the slot matching above was).
      const inCart = cartItems.some(item => {
        const itemStart = new Date(item.startDateTime);
        const itemEnd = new Date(item.endDateTime);

        // Grid slot is "in cart" if it starts at or after item start AND starts before item end
        return startDate >= itemStart && startDate < itemEnd;
      });

      let status: 'available' | 'in-cart' | 'reserved' | 'unavailable' = 'unavailable';
      let availableQuantity = 0;
      let totalQuantity = 0;
      let price = 0;
      let windowId = dailyWindow._id;

      if (matchingSlot) {
        windowId = matchingSlot.windowId;
        availableQuantity = matchingSlot.availableQuantity;
        totalQuantity = matchingSlot.totalQuantity;
        price = matchingSlot.price;

        if (inCart) {
          status = 'in-cart';
        } else if (availableQuantity > 0) {
          status = 'available';
        } else {
          status = 'reserved';
        }
      }

      gridSlots.push({
        time: timeLabel,
        startDateTime: startDate,
        endDateTime: endDate,
        windowId,
        price,
        availableQuantity,
        totalQuantity,
        status,
      });

      // Move to next interval
      currentMin = intervalEndMin;
      currentHour = intervalEndHour;
    }

    return gridSlots;
  }, [dailyWindow, slots, selectedDate, cartItems, timezone]);

  const handleSlotClick = (gridSlot: GridSlot) => {
    if (gridSlot.status === 'unavailable' || gridSlot.status === 'reserved') {
      return;
    }

    // Navigate to reservation page with selected start time as query parameter.
    // Use the same local Y-M-D the calendar / fetchSlotsForDate already used —
    // toISOString() is UTC and can roll a local-midnight calendar pick back
    // a day when the browser is east of UTC.
    const dateStr = [
      selectedDate.getFullYear(),
      String(selectedDate.getMonth() + 1).padStart(2, '0'),
      String(selectedDate.getDate()).padStart(2, '0'),
    ].join('-');
    const startTimeISO = gridSlot.startDateTime.toISOString();
    navigate(`/${listingName}/reserve/${gridSlot.windowId}/${dateStr}?startTime=${encodeURIComponent(startTimeISO)}`);
  };

  if (!dailyWindow) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header with date navigation */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
        <button
          onClick={onPrevDay}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous day"
        >
          <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
        </button>
        
        <h3 className="text-lg font-semibold text-gray-900">
          {formatDateLong(selectedDate)}
        </h3>
        
        <button
          onClick={onNextDay}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next day"
        >
          <ChevronRightIcon className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Grid */}
      <div className="overflow-y-auto max-h-[600px]">
        <div className="min-w-full">
          {gridSlots.map((gridSlot, index) => (
            <div
              key={index}
              className={`flex border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                gridSlot.status === 'unavailable' || gridSlot.status === 'reserved'
                  ? 'cursor-not-allowed opacity-60'
                  : 'cursor-pointer'
              }`}
              onClick={() => handleSlotClick(gridSlot)}
            >
              {/* Time label - fixed width */}
              <div className="w-24 sm:w-32 flex-shrink-0 py-3 px-3 sm:px-4 bg-gray-50 border-r border-gray-200">
                <span className="text-sm font-medium text-gray-700">{gridSlot.time}</span>
              </div>

              {/* Slot content */}
              <div className="flex-1 py-3 px-3 sm:px-4 flex items-center justify-between min-h-[44px]">
                <div className="flex items-center gap-2">
                  {gridSlot.status === 'available' && (
                    <>
                      <span className="font-medium text-primary-600">Reserve</span>
                      {gridSlot.availableQuantity > 1 && (
                        <span className="text-sm text-gray-500">
                          ({gridSlot.availableQuantity} available)
                        </span>
                      )}
                    </>
                  )}
                  {gridSlot.status === 'in-cart' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      In Cart
                    </span>
                  )}
                  {gridSlot.status === 'reserved' && (
                    <span className="text-gray-500">Reserved</span>
                  )}
                  {gridSlot.status === 'unavailable' && (
                    <span className="text-gray-400">Unavailable</span>
                  )}
                </div>

                {/* Price - only show for available/in-cart if there's space */}
                {(gridSlot.status === 'available' || gridSlot.status === 'in-cart') && (
                  <span className="hidden sm:inline text-sm font-medium text-gray-700">
                    ${gridSlot.price.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {gridSlots.length === 0 && (
        <div className="py-8 text-center text-gray-500">
          No time slots available for this date
        </div>
      )}
    </div>
  );
};

export default TimeSlotGrid;

